import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getGiftPayment, isOk, messageOf, startGiftCheckout } from "../../services/gift";
import GiftShell, { Check } from "./components/GiftShell";
import "./gift.css";

const METHOD_IMAGES = {
  stripe: { src: "/integrationimages/stripe.png", alt: "Pay with Stripe" },
  paypal: { src: "/integrationimages/paypal.png", alt: "Pay with PayPal" },
  square: { src: "/integrationimages/square-black.jpg", alt: "Pay with Square" },
};

// "Back": a logged-in purchaser goes to My Gifts; one who came from the
// email goes back to the previous page, or VerySoul's home in a fresh tab.
function BackLink() {
  const loggedIn = Boolean(localStorage.getItem("access_token"));
  const goBack = (e) => {
    if (loggedIn) return;
    e.preventDefault();
    if (window.history.length > 1 && document.referrer) {
      window.history.back();
    } else {
      window.location.href = "https://verysoul.com/";
    }
  };

  return (
    <a className="vsr-link vsr-back-link" href={loggedIn ? "/my-gifts" : "https://verysoul.com/"} onClick={goBack}>
      &larr; {loggedIn ? "Back to My Gifts" : "Back"}
    </a>
  );
}

// A result card (paid / closed / invalid link) in the public gift page frame.
function ResultCard({ tone, title, children }) {
  return (
    <GiftShell>
      <div className="vsr-back-row">
        <BackLink />
      </div>
      <div className="vsr-center">
        <section className="vsr-card vsr-reserved">
          <span className={`vsr-icon-badge ${tone === "success" ? "vsr-icon-badge-success" : ""}`} aria-hidden="true">
            {tone === "success" ? (
              <Check />
            ) : (
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="12.5" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </span>
          <h1 className="vsr-title">{title}</h1>
          {children}
          <div className="vsr-actions">
            <a className="vsr-btn vsr-btn-outline" href="https://verysoul.com/">
              Explore VerySoul
            </a>
          </div>
        </section>
      </div>
    </GiftShell>
  );
}

// "Complete My Gift" — opened from the purchaser's payment-request email or
// My Gifts (/gift-pay/:token). Public: the private token is the credential.
// The price is fixed by the backend and can't be edited here.
export default function GiftPay() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    getGiftPayment(token).then((res) => {
      if (isOk(res)) {
        setInfo(res.data.data);
      } else {
        setError(messageOf(res, "This payment link is not valid."));
      }
    });
  }, [token]);

  const pay = async (method) => {
    setBusy(method);
    const res = await startGiftCheckout(token, method);
    if (isOk(res) && res.data.data && res.data.data.approvedUrl) {
      window.open(res.data.data.approvedUrl, "_self");
    } else {
      setBusy("");
      toast.error(messageOf(res, "We could not start the payment. Please try again."));
    }
  };

  if (error) {
    return (
      <ResultCard title="Payment link not valid">
        <p className="vsr-lead">{error}</p>
        <div className="vsr-note">
          <p>If you think this is a mistake, please contact VerySoul directly rather than replying to any message.</p>
        </div>
      </ResultCard>
    );
  }

  if (!info) {
    return (
      <GiftShell>
        <p className="vsr-muted vsr-loading">Loading…</p>
      </GiftShell>
    );
  }

  if (info.status === "paid") {
    return (
      <ResultCard tone="success" title="Your VerySoul Gift is complete">
        <p className="vsr-lead">
          The session with <b>{info.medium_name}</b> on {info.date} at {info.time} is confirmed.
        </p>
        <blockquote className="vsr-quote">Thank you for giving someone the opportunity to explore.</blockquote>
      </ResultCard>
    );
  }

  if (info.status === "closed") {
    return (
      <ResultCard title="This payment request has closed">
        <p className="vsr-lead">No payment is needed for this request.</p>
        <div className="vsr-note">
          <p>Your gift remains available for your recipient to use another time.</p>
        </div>
      </ResultCard>
    );
  }

  const covered = info.max_price && info.max_price !== info.price;

  return (
    <GiftShell>
      <div className="vsr-stack vsr-pay">
        <div className="vsr-pay-head">
          <BackLink />
          <p className="vsr-eyebrow vsr-eyebrow-left">Complete My Gift</p>
          <h1 className="vsr-title vsr-title-left">Someone is ready to use your VerySoul Gift</h1>
          <p className="vsr-lead vsr-lead-left">Please check the details below before you pay.</p>
        </div>

        <div className="vsr-pay-grid">
          <div className="vsr-stack">
            <section className="vsr-pay-verify" aria-labelledby="vsr-verify-title">
              <svg className="vsr-pay-verify-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z" />
                <line x1="12" y1="8.5" x2="12" y2="12.5" />
                <line x1="12" y1="15.5" x2="12.01" y2="15.5" />
              </svg>
              <div>
                <h2 id="vsr-verify-title">Do you recognise this email address?</h2>
                <p>The person requesting the session used:</p>
                <p className="vsr-pay-email">{info.recipient_email}</p>
                <p>
                  Make sure it belongs to the person you gave your gift to. <b>If you don't recognise it, don't pay</b> — come directly to
                  VerySoul for help.
                </p>
              </div>
            </section>

            <section className="vsr-card vsr-pay-session">
              <p className="vsr-eyebrow vsr-eyebrow-left">They've selected</p>
              <p className="vsr-summary-name">{info.medium_name}</p>
              {info.service_name && (
                <p className="vsr-muted">
                  {info.service_name}
                  {info.duration ? ` — ${info.duration} min` : ""}
                </p>
              )}
              <dl className="vsr-summary-list">
                <dt>Date</dt>
                <dd>{info.date}</dd>
                <dt>Time</dt>
                <dd>{info.time}</dd>
                {info.timezone && (
                  <>
                    <dt>Timezone</dt>
                    <dd>{info.timezone}</dd>
                  </>
                )}
              </dl>
            </section>
          </div>

          <aside className="vsr-card vsr-pay-box">
            <p className="vsr-eyebrow vsr-eyebrow-left">Session price</p>
            <p className="vsr-pay-amount">
              {info.currency} {info.price}
            </p>
            {covered && (
              <p className="vsr-pay-covered">
                You offered to cover up to {info.currency} {info.max_price}. <b>{info.currency} {info.price} is all you'll pay.</b>
              </p>
            )}
            <p className="vsr-pay-deadline">Please pay by {info.pay_by} so we can finalize the session.</p>

            <p className="vsr-label vsr-pay-methods-label">Pay with</p>
            <div className="vsr-pay-methods">
              {info.payment_methods.map((method) => {
                const image = METHOD_IMAGES[method];
                return (
                  <button
                    key={method}
                    type="button"
                    className="vsr-pay-method"
                    disabled={Boolean(busy)}
                    aria-label={image ? image.alt : method}
                    onClick={() => pay(method)}
                  >
                    {busy === method ? "Opening…" : image ? <img src={image.src} alt="" /> : method}
                  </button>
                );
              })}
            </div>
            <p className="vsr-hint">
              {info.pay_forward
                ? "Your payment is processed through VerySoul's secure checkout on behalf of this medium, exactly as it would be for any client booking this session."
                : "You'll pay the medium directly. VerySoul does not receive or hold your session payment or payment information."}
            </p>
          </aside>
        </div>
      </div>
    </GiftShell>
  );
}
